import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Shield,
  Key,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Role, Permission } from "@/types/auth";
import {
  useRoles,
  usePermissions,
  useCreateRole,
  useUpdateRole,
  useRole,
} from "@/api-services/hooks/useRoles";
import { RoleDTO } from "@/api-services/hooks/adminTypes";

const AddEditRolePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const roleId = id ? parseInt(id, 10) : undefined;

  // Form state
  const [formData, setFormData] = useState({
    roleName: "",
    roleDescription: "",
    rolePermissions: "",
    permissions: [] as string[],
    restricted: [] as string[],
    remarks: "",
    status: "",
  });

  // Validation error state
  const [roleNameError, setRoleNameError] = useState<string>("");

  // API hooks
  const rolesQuery = useRoles({
    pageable: { page: 0, size: 1000 },
  });

  const permissionsQuery = usePermissions({});

  const { data: roleData, isLoading: isLoadingRole } = useRole(roleId || 0, {
    enabled: isEditMode && !!roleId,
  });

  const createRoleMutation = useCreateRole({
    onSuccess: () => {
      toast.success("Role created successfully!");
      navigate("/admin/users/roles");
    },
    onError: (error) => {
      toast.error(`Failed to create role: ${error.message}`);
    },
  });

  const updateRoleMutation = useUpdateRole({
    onSuccess: () => {
      toast.success("Role updated successfully!");
      navigate("/admin/users/roles");
    },
    onError: (error) => {
      toast.error(`Failed to update role: ${error.message}`);
    },
  });

  // Convert API Role to UI Role format
  const convertApiRoleToUIRole = (apiRole: any): Role => {
    const apiRoleId = apiRole.roleId ?? apiRole.id;
    const roleIdValue = apiRoleId !== undefined && apiRoleId !== null ? String(apiRoleId) : '';

    const role: Role = {
      id: roleIdValue,
      name: apiRole.roleName || apiRole.name || '',
      description: apiRole.roleDescription || apiRole.description || '',
      isSystemRole: apiRole.isSystemRole || false,
      permissions: Array.isArray(apiRole.permissions)
        ? apiRole.permissions.map((p: any) =>
            typeof p === 'string'
              ? { id: '', name: p, resource: '', action: '', description: '' }
              : p
          )
        : [],
      createdAt: apiRole.createdAt || new Date().toISOString(),
      updatedAt: apiRole.updatedAt || new Date().toISOString(),
    };

    (role as any).roleId = apiRoleId !== undefined && apiRoleId !== null 
      ? (typeof apiRoleId === 'number' ? apiRoleId : parseInt(String(apiRoleId), 10))
      : null;
    (role as any).status = apiRole.status || '';
    (role as any).rolePermissions = apiRole.rolePermissions || '';
    (role as any).restricted = Array.isArray(apiRole.restricted) ? apiRole.restricted : [];
    (role as any).isDeletedValue = apiRole.isDeletedValue || false;
    (role as any).remarks = apiRole.remarks || '';

    return role;
  };

  const roles: Role[] = rolesQuery.data?.content?.map(convertApiRoleToUIRole) || [];
  const permissions: Permission[] = permissionsQuery.data?.map((p: any) => ({
    id: String(p.id),
    name: p.name,
    resource: p.resource,
    action: p.action,
    description: p.description,
    createdAt: p.createdAt || new Date().toISOString(),
    updatedAt: p.updatedAt || new Date().toISOString(),
  })) || [];

  // Load role data for editing
  useEffect(() => {
    if (roleData && isEditMode) {
      const role = convertApiRoleToUIRole(roleData);
      const roleName = role.name || "";
      const roleDescription = role.description || "";
      const permissionNames = role.permissions.map((p) => p.name);
      const apiData = role as any;

      setFormData({
        roleName,
        roleDescription,
        rolePermissions: apiData.rolePermissions || "",
        permissions: permissionNames,
        restricted: Array.isArray(apiData.restricted) ? apiData.restricted : [],
        remarks: apiData.remarks || "",
        status: apiData.status || "",
      });
    }
  }, [roleData, isEditMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate role name uniqueness
    const trimmedRoleName = formData.roleName.trim().toLowerCase();
    const duplicateRole = roles.find((role) => {
      const existingRoleName = (role.name || '').trim().toLowerCase();
      if (isEditMode && roleData) {
        const editingRole = convertApiRoleToUIRole(roleData);
        const editingRoleData = editingRole as any;
        const editingRoleId = editingRoleData.roleId ?? editingRole.id;
        const roleIdCheck = (role as any).roleId ?? role.id;
        return existingRoleName === trimmedRoleName && roleIdCheck !== editingRoleId;
      }
      return existingRoleName === trimmedRoleName;
    });

    if (duplicateRole) {
      setRoleNameError(`Role name "${formData.roleName}" already exists. Please use a unique name.`);
      toast.error(`Role name "${formData.roleName}" already exists.`);
      return;
    }

    setRoleNameError("");

    if (isEditMode && roleId) {
      const roleData: RoleDTO = {
        roleId: roleId,
        roleName: formData.roleName,
        roleDescription: formData.roleDescription,
        rolePermissions: formData.rolePermissions || undefined,
        permissions: formData.permissions.length > 0 ? formData.permissions : undefined,
        restricted: formData.restricted.length > 0 ? formData.restricted : undefined,
        remarks: formData.remarks || undefined,
        status: formData.status || undefined,
      };

      await updateRoleMutation.mutate({
        id: roleId,
        ...roleData,
      });
    } else {
      const roleData: RoleDTO = {
        roleName: formData.roleName,
        roleDescription: formData.roleDescription,
        rolePermissions: formData.rolePermissions || undefined,
        permissions: formData.permissions.length > 0 ? formData.permissions : undefined,
        restricted: formData.restricted.length > 0 ? formData.restricted : undefined,
        remarks: formData.remarks || undefined,
        status: formData.status || undefined,
      };

      await createRoleMutation.mutate(roleData);
    }
  };

  const handlePermissionToggle = (permission: Permission, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      permissions: checked
        ? [...prev.permissions, permission.name]
        : prev.permissions.filter((name) => name !== permission.name),
    }));
  };

  const handleRestrictedToggle = (permission: Permission, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      restricted: checked
        ? [...prev.restricted, permission.name]
        : prev.restricted.filter((name) => name !== permission.name),
    }));
  };

  const getPermissionsByResource = useCallback(() => {
    const grouped = permissions.reduce((acc, permission) => {
      if (!acc[permission.resource]) {
        acc[permission.resource] = [];
      }
      acc[permission.resource].push(permission);
      return acc;
    }, {} as Record<string, Permission[]>);

    return Object.entries(grouped);
  }, [permissions]);

  const getPermissionIcon = (action: string) => {
    return <Key className="h-4 w-4" />;
  };

  if (isLoadingRole) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading role...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin/users/roles")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {isEditMode ? "Edit Role" : "Add New Role"}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isEditMode
                ? "Update the role information and permissions below."
                : "Create a new role with specific permissions."}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/users/roles")}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              createRoleMutation.isPending || 
              updateRoleMutation.isPending || 
              !!roleNameError
            }
          >
            <Save className="w-4 h-4 mr-2" />
            {createRoleMutation.isPending || updateRoleMutation.isPending
              ? "Saving..."
              : isEditMode
              ? "Update Role"
              : "Create Role"}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Enter the role name and description</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Role Name *</label>
                <Input
                  value={formData.roleName}
                  onChange={(e) => {
                    const newRoleName = e.target.value;
                    setFormData((prev) => ({ ...prev, roleName: newRoleName }));
                    
                    if (roleNameError) {
                      setRoleNameError("");
                    }

                    if (newRoleName.trim()) {
                      const trimmedRoleName = newRoleName.trim().toLowerCase();
                      const duplicateRole = roles.find((role) => {
                        const existingRoleName = (role.name || '').trim().toLowerCase();
                        if (isEditMode && roleData) {
                          const editingRole = convertApiRoleToUIRole(roleData);
                          const editingRoleData = editingRole as any;
                          const editingRoleId = editingRoleData.roleId ?? editingRole.id;
                          const roleIdCheck = (role as any).roleId ?? role.id;
                          return existingRoleName === trimmedRoleName && roleIdCheck !== editingRoleId;
                        }
                        return existingRoleName === trimmedRoleName;
                      });

                      if (duplicateRole) {
                        setRoleNameError(`Role name "${newRoleName.trim()}" already exists.`);
                      }
                    }
                  }}
                  placeholder="Driver Manager"
                  required
                  className={roleNameError ? "border-red-500 focus-visible:ring-red-500" : ""}
                />
                {roleNameError && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <span className="text-red-500">⚠</span>
                    {roleNameError}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">Status</label>
                <Select
                  value={formData.status}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, status: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Role Description *</label>
              <textarea
                value={formData.roleDescription}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    roleDescription: e.target.value,
                  }))
                }
                placeholder="Describe the role and its responsibilities..."
                className="mt-1 min-h-[100px] w-full px-3 py-2 border border-border rounded-md bg-background"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium">Role Permissions (Optional)</label>
              <Input
                value={formData.rolePermissions}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, rolePermissions: e.target.value }))
                }
                placeholder="Enter role permissions (comma-separated)"
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Permissions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Permissions
            </CardTitle>
            <CardDescription>
              Select the permissions this role should have
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {getPermissionsByResource().map(
                ([resource, resourcePermissions]) => (
                  <div key={resource} className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 font-medium text-lg mb-4">
                      <Shield className="h-5 w-5 text-primary" />
                      {resource.charAt(0).toUpperCase() + resource.slice(1)}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {resourcePermissions.map((permission) => (
                        <div
                          key={permission.id}
                          className="flex items-center justify-between p-3 border rounded hover:bg-muted/50"
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              id={`perm-${permission.id}`}
                              checked={formData.permissions.includes(permission.name)}
                              onCheckedChange={(checked) =>
                                handlePermissionToggle(
                                  permission,
                                  checked === true
                                )
                              }
                            />
                            <label
                              htmlFor={`perm-${permission.id}`}
                              className="text-sm cursor-pointer"
                            >
                              <div className="font-medium">
                                {permission.name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {permission.description}
                              </div>
                            </label>
                          </div>
                          <div className="flex items-center gap-2">
                            {getPermissionIcon(permission.action)}
                            <span className="text-xs text-muted-foreground capitalize">
                              {permission.action}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          </CardContent>
        </Card>

        {/* Restricted Permissions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Restricted Permissions
            </CardTitle>
            <CardDescription>
              Select permissions that should be restricted for this role
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {getPermissionsByResource().map(
                ([resource, resourcePermissions]) => (
                  <div key={resource} className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 font-medium text-lg mb-4">
                      <Shield className="h-5 w-5 text-primary" />
                      {resource.charAt(0).toUpperCase() + resource.slice(1)}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {resourcePermissions.map((permission) => (
                        <div
                          key={permission.id}
                          className="flex items-center justify-between p-3 border rounded hover:bg-muted/50"
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              id={`restricted-${permission.id}`}
                              checked={formData.restricted.includes(permission.name)}
                              onCheckedChange={(checked) =>
                                handleRestrictedToggle(
                                  permission,
                                  checked === true
                                )
                              }
                            />
                            <label
                              htmlFor={`restricted-${permission.id}`}
                              className="text-sm cursor-pointer"
                            >
                              <div className="font-medium">
                                {permission.name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {permission.description}
                              </div>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          </CardContent>
        </Card>

        {/* Additional Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Additional Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium">Remarks</label>
              <textarea
                value={formData.remarks}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    remarks: e.target.value,
                  }))
                }
                placeholder="Additional remarks or notes..."
                className="mt-1 min-h-[80px] w-full px-3 py-2 border border-border rounded-md bg-background"
              />
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/users/roles")}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              createRoleMutation.isPending || 
              updateRoleMutation.isPending || 
              !!roleNameError
            }
          >
            <Save className="w-4 h-4 mr-2" />
            {createRoleMutation.isPending || updateRoleMutation.isPending
              ? "Saving..."
              : isEditMode
              ? "Update Role"
              : "Create Role"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddEditRolePage;

